import { useContext, useMemo } from 'react';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKKontodelREDOVISN_TYPEnum,
} from '../../../../../../../../globalQueryTypes';
import { IColumnDef } from '../../../../../../../../Types/ColumnDefType';
import { IAssociationListConfig } from '../../../../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { useBusinessDataFormContext } from '../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import KonteringContext from '../../../../../../_Common/Components/KonteringManager/KonteringContext';
import useRowFunctionMenuItems from '../../../Hooks/useRowFunctionMenuItems';
import { BokforderType__Associations_Rader } from '../../../Queries/_QueryTypes/BokforderType';
import { isCellDisabledBfo } from '../../../Utils/BfoUtanordningUtils';
import {
  getBokforderCustomColumns,
  getKontodelCustomConfigs,
  getUsedKontodelarByBokforder,
} from '../../../Utils/BokforderUtils';
import BokforderContext from '../../BokforderContext/BokforderContext';
import { IUserBokforderKodbehorigheter } from '../../BokforderContext/BokforderContextManager';
import { ActiveDatabaseContext } from '../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

interface IProps {
  editKontering: boolean;
}

export const useUtanordningAssociationListConfig = (
  props: IProps
): IAssociationListConfig => {
  const { userID } = useContext(ActiveDatabaseContext);
  const bokforderContext = useContext(BokforderContext);
  const bokforderContextKonteringsmallarData =
    bokforderContext.konteringsmallarData;
  const formContext = useBusinessDataFormContext();
  const konteringContext = useContext(KonteringContext);

  const usersWithKodbehorigheter: IUserBokforderKodbehorigheter[] = useMemo(
    () => bokforderContext.allUtanordningUsersWithKodbeh,
    [bokforderContext.allUtanordningUsersWithKodbeh]
  );

  const attestRows: BokforderType__Associations_Rader[] = useMemo(
    () =>
      formContext?.data?._Associations?.Rader?.filter(
        (row: any) => row?._Associations?.Attestant?.id === userID
      ) ?? [],
    [formContext?.data?._Associations?.Rader, userID]
  );

  const haveAttesterat = useMemo(
    () => attestRows.every(row => row.ATTESTERAD_DATUM != null),
    [attestRows]
  );

  const rowHasReserv = useMemo(
    () =>
      formContext?.data?._Associations?.Rader?.some(
        (row: BokforderType__Associations_Rader) =>
          row._Associations?.Reserv != null
      ),
    [formContext?.data?._Associations?.Rader]
  );

  return useMemo(() => {
    const usedKontodelar = getUsedKontodelarByBokforder(
      formContext?.data?._Associations
        ?.Rader as BokforderType__Associations_Rader[]
    );

    const isAttest =
      formContext?.data?.STATUS === TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST &&
      bokforderContext.isAttestant;

    const isReservAttest =
      formContext?.data?.STATUS === TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST &&
      bokforderContext.isReservForAttestant;

    const allowedStatuses = [
      TEDEKBokforderSTATUSEnum.UTKAST,
      TEDEKBokforderSTATUSEnum.EJ_GODKAND,
      TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
    ];

    const isReservAndHaveAttesterat = isReservAttest
      ? bokforderContext.everyReservAttesterIsAttesterade
      : !allowedStatuses.includes(formContext?.data?.STATUS) &&
        !formContext?.isNew;

    const attestantHaveAttesterat = isAttest
      ? haveAttesterat
      : !allowedStatuses.includes(formContext?.data?.STATUS) &&
        !formContext?.isNew;

    const editSwitchTurnedOn =
      !bokforderContext.showAttestantKonteraSwitch ||
      (bokforderContext.showAttestantKonteraSwitch && !props.editKontering);

    const disableEdit =
      (attestantHaveAttesterat && isReservAndHaveAttesterat) ||
      ((isAttest || isReservAttest) && editSwitchTurnedOn);

    const kontodelDefFromKodplan =
      konteringContext.konteringUtils.getKontodelDefsByKodplanId(
        bokforderContext.bokforderKodplan?.id ?? ''
      );

    const customDefaultColumns: IColumnDef[] = getBokforderCustomColumns(
      kontodelDefFromKodplan,
      bokforderContext.bokforderDate,
      usersWithKodbehorigheter,
      disableEdit,
      bokforderContext.bokforderKodplan?.id ?? '',
      konteringContext.konteringUtils,
      bokforderContext.templateRows,
      usedKontodelar,
      rowHasReserv,
      true,
      'Belopp',
      [
        TEDEKKontodelREDOVISN_TYPEnum.RESULTATKONTO,
        TEDEKKontodelREDOVISN_TYPEnum.STALLNINGSKONTO,
      ],
      bokforderContext.showAttestantKonteraSwitch,
      bokforderContextKonteringsmallarData
    );

    const kontodelCustomConfigs = getKontodelCustomConfigs(
      kontodelDefFromKodplan
    );

    return {
      inCard: true,
      integrated:
        (isAttest || isReservAttest || bokforderContext.isEditMode) ?? false,
      defaultVisningstyp: 'tabell',
      customDefaultColumns: customDefaultColumns,
      useFunctionMenuItems: input =>
        useRowFunctionMenuItems(input, {
          disabled: editSwitchTurnedOn,
        }),
      customBusinessConfig: {
        associations: kontodelCustomConfigs,
      },
      sumColumns: [{ columnName: 'DEBET' }],
      isCellDisabled: params =>
        isCellDisabledBfo(
          params,
          formContext?.data?.STATUS,
          bokforderContext.showAttestantKonteraSwitch,
          props.editKontering,
          bokforderContext.bokforderVariant,
          []
        ),
    };
  }, [
    formContext?.data?.STATUS,
    formContext?.data?._Associations?.Rader,
    formContext?.isNew,
    bokforderContext.isAttestant,
    bokforderContext.isReservForAttestant,
    bokforderContext.everyReservAttesterIsAttesterade,
    bokforderContext.bokforderKodplan?.id,
    bokforderContext.bokforderDate,
    bokforderContext.templateRows,
    bokforderContext.showAttestantKonteraSwitch,
    bokforderContext.isEditMode,
    bokforderContext.bokforderVariant,
    haveAttesterat,
    props.editKontering,
    konteringContext.konteringUtils,
    usersWithKodbehorigheter,
    rowHasReserv,
    bokforderContextKonteringsmallarData,
  ]);
};
